import { IEventrActivity } from '@eventr/common'
import { Gutters, Typography } from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Virtuoso } from 'react-virtuoso'
import { useShallow } from 'zustand/react/shallow'
import { useGlobalRouter } from '../../../feat/globalRouter/useGlobalRouter'
import { NavigationView } from '../../../feat/layout/NavigationView'
import { SearchBar } from '../../../home/components/carousel/slide/footer/HeaderSearchBar'
import { sectionTranslation } from '../../../i18n/translateHelpers'
import { useBoundStore } from '../../../state/bound.store'
import {
    selectCategoryActivities,
    selectorSplitData,
} from '../../../state/itineraryState/currentItin.slice'
import { userPrefShowDestTime } from '../../../state/userPrefsState/userPrefsSignels'
import { useFilterElements } from '../../hooks/useFilterElements'
import { usePastPresentElements } from '../../hooks/usePastPresentElements'
import { ElementRenderer } from '../../renderers/elementRender'
import { ScreenType } from '../../renderers/rendererLocator'
import { SectionV2Provider } from '../../state/sectionProvider'
import { getDateTimeISOFromEventrDateStringAndZone } from '../../upNextRenderer'
import { ItemsDisclosureGroup } from './disclosureGroupItems'
interface IVirtualItem {
    id: string | number
    data: IEventrActivity
    isPast: boolean
}

export interface IGroupedVirtualItem {
    date: string
    items: IVirtualItem[]
}
export const SectionItemsPage = () => {
    const { section_id, cat_id } = useParams<{
        section_id: string
        cat_id: string
    }>()
    const [search] = useSearchParams()
    const { goBack } = useGlobalRouter()
    const { section, selectedV2 } = useBoundStore(
        useShallow((s) => {
            return {
                section: s.sections.find((s) => s.id === Number(section_id)),
                selectedV2: s.selectedItinerary?.v2?.find(
                    (s) => s.sectionID === Number(section_id)
                ),
            }
        })
    )

    const { categorized, splitSections } = useBoundStore(selectorSplitData)

    const sectionV2 = categorized?.find(
        (s) => s.sectionID === Number(section_id) && !s.category
    )

    const activitiesForCategory = useBoundStore((state) =>
        selectCategoryActivities(
            state,
            cat_id,
            search.get('ogCode') ?? undefined
        )
    )

    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    useEffect(() => {
        // Set a timer to update the debounced query after 300ms (adjust as needed)
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        // Cleanup function: clear the timeout if searchQuery changes before the timer runs out
        return () => {
            clearTimeout(handler)
        }
    }, [searchQuery])
    const dataToUse = useMemo(() => {
        const ogCode = search.get('ogCode')
        if (cat_id) {
            return activitiesForCategory
        }
        if (ogCode) {
            const ogSection = splitSections?.find((s) => s.itinCode === ogCode)
            return ogSection?.data.find(
                (s) => s.sectionID === Number(section_id) && !s.category
            )
        }
        return sectionV2
    }, [
        cat_id,
        sectionV2,
        activitiesForCategory,
        search,
        section_id,
        splitSections,
    ])

    const { pastElements, presentElements } = usePastPresentElements(
        dataToUse ?? null,
        (search.get('screen') as ScreenType) ?? 'personal'
    )

    const filteredPresentElements = useFilterElements<IEventrActivity>(
        presentElements,
        debouncedSearchQuery,
        Number(section_id)
    )
    const filteredPastElements = useFilterElements<IEventrActivity>(
        pastElements,
        debouncedSearchQuery,
        Number(section_id)
    )
    // Combine present and past elements for Virtuoso
    const mapToVirtualItem = (
        item: IEventrActivity,
        index: number,
        isPast: boolean
    ): IVirtualItem => ({
        id: item._id ?? `${isPast ? 'past' : 'present'}-${index}`,
        data: item,
        isPast,
    })

    const groupItemsByDate = (
        items: IVirtualItem[]
    ): Record<string, IVirtualItem[]> => {
        const grouped: Record<string, IVirtualItem[]> = {}
        items.forEach((item) => {
            let key = 'Stands'
            if (item.data.start) {
                key = getDateTimeISOFromEventrDateStringAndZone(
                    item.data.start,
                    userPrefShowDestTime.value
                ).split(' ')[0]
            }
            if (!grouped[key]) {
                grouped[key] = []
            }
            grouped[key].push(item)
        })
        return grouped
    }

    const sortGroupedItems = (
        a: IGroupedVirtualItem,
        b: IGroupedVirtualItem
    ): number => {
        const getCategoryValue = (group: IGroupedVirtualItem) => {
            if (group.date === 'Stands') return 1 // Stands
            if (group.items.some((item) => !item.isPast)) return 0 // Present
            return 2 // Past
        }

        const valA = getCategoryValue(a)
        const valB = getCategoryValue(b)

        if (valA !== valB) {
            return valA - valB // Sorts by category: 0 (Present) < 1 (Stands) < 2 (Past)
        }

        // If in the same category (Present or Past) and not 'Stands', sort by date.
        if (valA === 0 || valA === 2) {
            return new Date(a.date).getTime() - new Date(b.date).getTime()
        }

        return 0 // Should not happen if 'Stands' is handled correctly
    }

    // Combine present and past elements for Virtuoso
    const virtualItems: IGroupedVirtualItem[] = useMemo(() => {
        const presentVirtualItems = filteredPresentElements.map((item, index) =>
            mapToVirtualItem(item, index, false)
        )
        const pastVirtualItems = filteredPastElements.map((item, index) =>
            mapToVirtualItem(item, index, true)
        )

        const allVirtualItems = [...presentVirtualItems, ...pastVirtualItems]
        const groupedByDay = groupItemsByDate(allVirtualItems)

        return Object.entries(groupedByDay)
            .map(([date, items]) => ({
                date,
                items,
            }))
            .sort(sortGroupedItems)
    }, [filteredPresentElements, filteredPastElements])

    const title = useMemo(() => {
        if (cat_id) {
            return activitiesForCategory?.sectionNameOverwrite
        }
        return sectionTranslation('Activities', {
            count: 0,
        })
    }, [cat_id, section, activitiesForCategory?.sectionNameOverwrite])
    // Check if there's anything to display
    const hasItems = virtualItems.length > 0
    const showSearchBar = presentElements.length + pastElements.length > 2
    return (
        <NavigationView
            isRoot
            usePageTransition
            navigationBarTitle={title}
            backButtonAction={() => {
                goBack(true)
            }}
        >
            <Gutters
                topPadding="0px"
                style={{
                    height: 'calc(100vh - 130px)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <SectionV2Provider
                    sectionData={selectedV2}
                    isPublic={search.get('ogCode') ? true : false}
                >
                    {showSearchBar && (
                        <Box sx={{ p: 1 }}>
                            <SearchBar
                                value={searchQuery}
                                onChange={(v) => setSearchQuery(v)}
                            />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        {hasItems ? (
                            <Virtuoso
                                style={{ height: '100%' }}
                                data={virtualItems}
                                itemContent={(index, group) => (
                                    <Box key={group.date}>
                                        <ItemsDisclosureGroup
                                            group={group}
                                            index={index}
                                            content={group.items.map((item) => (
                                                <Box
                                                    key={item.id}
                                                    sx={{ padding: '0px 12px' }}
                                                >
                                                    <ElementRenderer
                                                        d={item.data}
                                                        components={
                                                            selectedV2
                                                                ?.rendererOptions
                                                                ?.components ??
                                                            []
                                                        }
                                                        screenType={'personal'}
                                                        past={item.isPast}
                                                    />
                                                </Box>
                                            ))}
                                        />
                                    </Box>
                                )}
                            />
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="bodyBold">
                                    {debouncedSearchQuery
                                        ? 'No items match your search.'
                                        : 'No items to display.'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </SectionV2Provider>
            </Gutters>
        </NavigationView>
    )
}
